// THE ASSET MANAGER — loads what tools/assets baked, before anything renders.
//
// The forge used to make every texture in the game while the player waited,
// which put a hard ceiling on all of them: a sheet had to finish inside a
// load screen, so it was small, and it was cheap, and it was simple. Baked
// offline there is no such clock. What replaces it is this module: a manifest
// fetch, a KTX2 decode, and a gate that will not let the game boot half
// dressed.
//
// NOTHING RENDERS HALF DRESSED. `loadBakedAssets()` resolves only when every
// shipped surface is on the GPU, and main.ts awaits it before `startGame()`.
// The alternative — render, then swap textures in as they arrive — is how you
// get a settlement that flashes grey for two seconds on every join, and it
// would also break `matchLuma()`, which needs a sheet's measured mean at the
// moment a material is built.
//
// FAILURE IS LOUD. If the manifest or a sheet does not load, this throws and
// the boot flow shows its error. There is deliberately no fall-back to the
// runtime forge: the whole point of porting a surface is that its pixels have
// exactly one origin, and a silent second path is how two of them drift.

import {
  ASSET_BASE,
  ASSET_MANIFEST_VERSION,
  type AssetManifest,
  SURFACE_CATALOG,
} from "@bebop/forge";
import * as THREE from "three";
import { KTX2Loader } from "three/addons/loaders/KTX2Loader.js";
import type { Surface } from "./forge";

const loaded = new Map<string, Surface>();
let foliageAtlas: THREE.Texture | undefined;
let bytes = 0;
let ready = false;
let sheet: AssetManifest | undefined;

/** The manifest, once loaded — render/character.ts reads its own section. */
export function manifest(): AssetManifest | undefined {
  return sheet;
}

/** Total encoded bytes fetched — reported by the boot gate and the perf HUD. */
export function bakedBytes(): number {
  return bytes;
}

export function assetsReady(): boolean {
  return ready;
}

/** A baked surface, or undefined if the catalog does not ship this one. */
export function bakedSurface(name: string): Surface | undefined {
  return loaded.get(name);
}

/**
 * The vegetation atlas, uploaded by the boot gate.
 *
 * CLAMPED, not repeated, and never disposed: it is one sheet shared by every
 * plant in every scene, and a Tracker that took it down would leave the fen
 * with no foliage on the next rebuild.
 */
export function bakedFoliage(): THREE.Texture | undefined {
  return foliageAtlas;
}

/**
 * Anisotropy matches what the runtime forge asked for, capped by the device.
 * Triplanar sampling means every sheet is seen at a grazing angle somewhere
 * in every frame, which is exactly the case anisotropic filtering exists for.
 *
 * THE GROUND CRUST ASKS FOR TWICE AS MUCH, and it is not a preference. A
 * horizontal plane running to a fog line is the worst case anisotropy exists
 * for — every texel past twenty metres is sampled at a grazing angle — and
 * the round-3 mesa verdict measured exactly what happens without it: the
 * sheet "breaking into dotted stipple lines at mid-distance". render/
 * textures.ts carried `anisotropy = 16` on those two maps with that note
 * attached, so the value travels with the port rather than quietly reverting
 * to the material default on the way through the manifest.
 */
function dressTexture(
  tex: THREE.Texture,
  srgb: boolean,
  maxAniso: number,
  aniso = 8,
): THREE.Texture {
  tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  // The mip chain is IN THE FILE. Asking three to generate one would be both
  // wasted work and impossible — you cannot box-filter a block-compressed
  // texture without decompressing it first.
  tex.generateMipmaps = false;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.anisotropy = Math.min(aniso, maxAniso);
  tex.needsUpdate = true;
  return tex;
}

/**
 * Fetch, transcode and upload every shipped surface.
 *
 * `renderer` is used only to ask the GPU which compressed formats it takes
 * (BC7/ASTC/ETC2/BC1 — the transcode target is chosen from that answer, and
 * it is why one file serves every device). It is not rendered with, so the
 * boot flow can hand over a throwaway context created for the question and
 * disposed after it: the game's real renderer does not exist yet at the
 * moment the assets have to be on the GPU.
 */
export async function loadBakedAssets(renderer?: THREE.WebGLRenderer): Promise<void> {
  if (ready) return;
  const probe = renderer ?? new THREE.WebGLRenderer();
  const maxAniso = probe.capabilities.getMaxAnisotropy();

  const res = await fetch(`${ASSET_BASE}manifest.json`, { cache: "no-cache" });
  if (!res.ok) throw new Error(`asset manifest ${res.status} — run \`pnpm assets:build\``);
  const loadedManifest = (await res.json()) as AssetManifest;
  if (loadedManifest.version !== ASSET_MANIFEST_VERSION) {
    throw new Error(
      `asset manifest is version ${loadedManifest.version}, this client wants ${ASSET_MANIFEST_VERSION}`,
    );
  }
  sheet = loadedManifest;

  // No `setTranscoderPath`: the loader resolves the Basis WASM and its JS
  // wrapper relative to its own module URL, which Vite rewrites — serving them
  // out of node_modules in dev and emitting them content-hashed at build. That
  // keeps the transcoder locked to the installed `three` (a mismatched one
  // fails inside a blob-URL worker, at boot, unhelpfully) and keeps ~590 kB of
  // vendored WASM out of the repository. It costs one extra request the first
  // time a KTX2 asset is loaded, and nothing after.
  const loader = new KTX2Loader().detectSupport(probe);
  // Four workers: the transcode is the only CPU work at boot and it is
  // embarrassingly parallel, but every worker also instantiates its own copy
  // of the WASM, and a phone with two cores pays for the ones it cannot use.
  loader.setWorkerLimit(4);

  try {
    // Every sheet in flight at once: three per surface, and the worker pool
    // is the thing that limits concurrency, not the request order.
    const jobs = Object.entries(loadedManifest.materials).map(async ([name, material]) => {
      const spec = SURFACE_CATALOG[name as keyof typeof SURFACE_CATALOG];
      const aniso = spec?.projection === "uv" ? 16 : 8;
      const [albedo, normal, orm] = await Promise.all(
        (["albedo", "normal", "orm"] as const).map(async (kind) => {
          const entry = material.maps[kind];
          const tex = await loader.loadAsync(ASSET_BASE + entry.url);
          bytes += entry.bytes;
          return dressTexture(tex, entry.srgb, maxAniso, aniso);
        }),
      );
      if (!albedo || !normal || !orm) throw new Error(`incomplete surface: ${name}`);
      loaded.set(name, {
        albedo,
        normal,
        orm,
        meters: material.meters,
        meanLinear: material.meanLinear,
        // The half of the BRDF that is not a texture — F0, sheen, and how
        // much of the environment probe this class returns. Measured and
        // written by the baker, exactly like `meanLinear`, and for the same
        // reason: the client never re-derives what it did not make.
        response: material.response,
      });
    });
    const foliage = loadedManifest.foliage;
    const atlasJob = foliage
      ? loader.loadAsync(ASSET_BASE + foliage.atlas.url).then((tex) => {
          bytes += foliage.atlas.bytes;
          tex.colorSpace = THREE.NoColorSpace;
          tex.wrapS = THREE.ClampToEdgeWrapping;
          tex.wrapT = THREE.ClampToEdgeWrapping;
          tex.generateMipmaps = false;
          tex.minFilter = THREE.LinearMipmapLinearFilter;
          tex.magFilter = THREE.LinearFilter;
          tex.anisotropy = Math.min(8, maxAniso);
          tex.needsUpdate = true;
          foliageAtlas = tex;
        })
      : Promise.resolve();
    await Promise.all([...jobs, atlasJob]);
  } finally {
    // Frees the worker pool and its WASM instances. The textures survive it.
    loader.dispose();
    if (!renderer) probe.dispose();
  }
  ready = true;
}
